import { KeyValuePipe, NgClass, NgStyle } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  forwardRef,
  inject,
  Input,
  model,
  OnInit,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import PrimeOverlayComponent from '@iupics-components/overrided/prime-overlay/prime-overlay.component';
import EditTabUiComponent from '@iupics-components/standard/layouts/edit-tab-ui/edit-tab-ui.component';
import ValuePreferencePanelComponent from '@iupics-components/standard/value-preference-panel/value-preference-panel.component';
import { SecurityManagerService } from '@iupics-manager/managers/security-manager/security-manager.service';
import { AbstractDataContainer, AbstractDataContainerCallout } from '@iupics-manager/models/abstract-datacontainer';
import { IupicsDataField } from '@iupics-manager/models/iupics-data';
import { DropdownComponent } from '@iupics-util/components/dropdown/dropdown.component';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipOptions } from 'primeng/api';
import { OverlayPanel } from 'primeng/overlaypanel';
import { TooltipModule } from 'primeng/tooltip';
import { LabelUiComponent } from '../label-ui/label-ui.component';

@Component({
  selector: 'iu-input-translations-ui',
  standalone: true,
  imports: [
    TranslateModule,
    TooltipModule,
    NgClass,
    NgStyle,
    PrimeOverlayComponent,
    forwardRef(() => ValuePreferencePanelComponent),
    DropdownComponent,
    LabelUiComponent,
    KeyValuePipe,
  ],
  templateUrl: './input-translations-ui.component.html',
  styleUrl: './input-translations-ui.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputTranslationsUiComponent extends AbstractDataContainer implements OnInit, AfterViewInit {
  readonly ADD_NEW_ENTRY_KEY = 'new' as const;

  readonly TOOLTIP_OPTIONS: TooltipOptions = {
    tooltipPosition: 'top',
    tooltipZIndex: '2100',
    escape: false,
    showDelay: 500,
    hideDelay: 250,
    life: 3000,
  };

  private readonly securityManagerService = inject(SecurityManagerService);

  @Input()
  columnName: string;
  @Input()
  placeHolder: string;
  @Input()
  data: IupicsDataField;
  @Input()
  isKeyUpTriggerable = false;

  defaultLang = signal(undefined);
  @Input()
  set defaultLanguage(value: string) {
    this.defaultLang.set(value);
  }

  readonly value = model<InputTranslationsValue>({});

  @Input()
  set fieldValue(value: string | InputTranslationsValue) {
    if (typeof value === 'string') {
      this.value.update((v) => {
        const key = Object.keys(v)?.[0];
        if (key) {
          v[key] = value;
        }

        return { ...v };
      });

      return;
    }

    this.value.set(value);
  }
  get fieldValue(): InputTranslationsValue {
    return this.value();
  }

  readonly open = signal(false);

  formGroup: FormGroup;

  dataContainers: AbstractDataContainerCallout;

  @ViewChild('input', { static: true })
  inputRef: ElementRef<HTMLInputElement>;
  @ViewChild('opConflict', { static: true })
  opConflict: OverlayPanel;

  readonly visibleValue = computed(() => this.value()[this.defaultLang()]);
  readonly otherValues = computed(() => {
    const mutualCondition = (i: string): boolean => i !== this.ADD_NEW_ENTRY_KEY && i !== this.defaultLang();
    return Object.keys(this.value()).reduce((acc, key) => {
      if (mutualCondition(key)) {
        acc[key] = this.value()[key];
      }
      return acc;
    }, {});
  });

  readonly options = computed(() => {
    const keys = Object.keys(this.value());
    return (
      this.data?.items.filter(
        (item) => item !== this.ADD_NEW_ENTRY_KEY && item !== this.defaultLang() && !keys.includes(item)
      ) ?? []
    );
  });

  readonly showTranslationsSection = computed(
    () => this.options().length > 0 || Object.keys(this.otherValues()).length > 0
  );

  // outputs
  @Input()
  readonly fieldValueChange = output<InputTranslationsValue | undefined>();

  ngOnInit(): void {
    super.ngOnInit();
    if (this.cssClass !== undefined) {
      this.cssGrid = this.cssClass;
    }
    this.cssClass = ' ' + this.cssGrid;
    this.setupDefaultLang();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    let parentComp = this.DOMParentComponent;
    while (parentComp && !(parentComp instanceof EditTabUiComponent)) {
      parentComp = parentComp.DOMParentComponent;
    }
    if (parentComp instanceof EditTabUiComponent) {
      this.dataContainers = new AbstractDataContainerCallout(parentComp.dataContainers);
    }

    this.addEntry();
  }

  private setupDefaultLang(): void {
    if (this.defaultLang()) {
      return;
    }

    const lang = this.securityManagerService.getIupicsDefaultLanguage();
    this.defaultLang.set(lang?.iso_code ?? 'en_US');
  }

  toggle(value: boolean): void {
    this.open.set(value);
    if (!value) {
      this.inputRef.nativeElement?.blur();
    }
  }

  addEntry(): void {
    if ((this.options()?.length ?? 0) === 0) {
      return;
    }

    const key = this.options().length === 1 ? this.options()[0] : this.ADD_NEW_ENTRY_KEY;
    this.value.update((value) => ({ ...value, [key]: '' }));
  }

  deleteEntry(key: string): void {
    this.value.update((value) => {
      if (key in value) {
        delete value[key];
      }

      return { ...value };
    });
  }

  showConflictPanel(ev: MouseEvent): void {
    this.opConflict.toggle(ev);
  }

  //#region Events
  onUserInput(event: InputEvent): void {
    if (
      this.data?.vFormat &&
      event.inputType === 'insertText' &&
      (event.target as HTMLInputElement).value.length > this.data.vFormat.length
    ) {
      (event.target as HTMLInputElement).value = (event.target as HTMLInputElement).value.slice(
        0,
        this.data.vFormat.length
      );
    }
  }

  onValueChange(event: Event, key: string): void {
    this.value.update((value) => ({ ...value, [key]: (event.target as HTMLInputElement).value }));
    this.dataChange(this.value());
  }

  onKeyUp(event: KeyboardEvent): void {
    if (this.isStandalone && this.isKeyUpTriggerable) {
      this.fieldValueChange.emit(this.getParsedValue(this.value()));
    }
  }

  onClick(event: MouseEvent, input: HTMLElement): void {
    if (event.button > 0) {
      return;
    }
    input.focus();
  }

  onKeyChange(language: { key: any; value: any }, event: Event): void;
  onKeyChange(language: { key: string; value: string }, event: InputEvent): void {
    this.value.update((v) => {
      delete v[language.key];
      return { ...v, [event.data]: language.value };
    });
  }
  //#endregion

  /**
   * Remove 'new' special key if exists and key with empty value
   * @param value
   * @returns
   */
  private getParsedValue(value: InputTranslationsValue): InputTranslationsValue {
    const vValue = { ...value };
    for (const key in vValue) {
      if (!vValue[key]) {
        continue;
      }

      if (key === this.ADD_NEW_ENTRY_KEY || vValue[key].trim() === '') {
        delete vValue[key];
      }
    }

    return vValue;
  }

  dataChange(value: InputTranslationsValue): void {
    super.dataChange(this.getParsedValue(value));
  }
}

export type InputTranslationsValue = Record<string, string>;
